// =============================================================================
// http-error.ts — Base d'erreur HTTP partagée (L-2, intercepteur 401 admin).
// =============================================================================
// Toutes les erreurs des clients ADMIN post-auth héritent de HttpApiError, qui CONSERVE le statut
// HTTP d'origine (écrit par chaque `toApiError`). L'intercepteur 401 (providers.tsx, QueryCache /
// MutationCache `onError`) lit ce statut via `isUnauthorized` pour ramener proprement à la landing
// quand une session admin expire EN COURS D'USAGE (DBU §2.1 l.590/592). Le gating UI n'est PAS la
// frontière de sécurité : l'autorité reste l'AdminSessionGuard serveur + le cookie httpOnly + le RLS.
//
// STATUT OPTIONNEL (`number | undefined`) : les erreurs FABRIQUÉES par les doubles de test (stubs,
// validations locales) n'ont pas de statut réseau — elles passent `undefined`. Seules les erreurs
// issues d'une réponse HTTP réelle (`toApiError`) portent le statut. La discrimination du 401 reste
// exacte (`status === 401` ⇒ `undefined` n'est jamais un faux positif). Ce choix préserve les ~60
// sites de construction existants (stubs `new XxxApiError(message)`) sans réécriture (minimal impact).

export class HttpApiError extends Error {
  /**
   * Statut HTTP d'origine quand connu (réponse réseau) ; `undefined` pour une erreur fabriquée hors
   * réseau (double de test, validation locale). Lu par l'intercepteur 401 (isUnauthorized).
   */
  public readonly status: number | undefined;

  public constructor(message: string, status?: number, name = 'HttpApiError') {
    super(message);
    this.name = name;
    this.status = status;
  }
}

/**
 * Type guard : erreur HTTP 401 (session admin absente/expirée). Pilote le redirect L-2 vers la
 * landing. Ne matche QUE les erreurs porteuses d'un statut réseau 401 (clients admin migrés vers
 * HttpApiError) : les erreurs supplier (kind:'unauthorized') et les erreurs sans statut sont ignorées.
 */
export const isUnauthorized = (error: unknown): error is HttpApiError =>
  error instanceof HttpApiError && error.status === 401;
